import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import api from '../../helpers/axiosconfig';
import { sendPasswordResetEmail } from '../../services/emailService';

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [notifyUpdates, setNotifyUpdates] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  

  const handleResetPassword = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsResetLoading(true);
    try {
      const token = Math.random().toString(36).substring(2, 8).toUpperCase();
      const result = await sendPasswordResetEmail(email, token);
      
      if (result.success) {
        alert('Password reset code sent to your email!');
        setShowResetPassword(false);
        setEmail('');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } catch (error) {
      setError('Failed to send reset email. Please try again.');
    } finally {
      setIsResetLoading(false);
    }
  };

  const handleMobileNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setMobileNumber(value);
      setError('');
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      if (showResetPassword) {
        handleResetPassword();
      } else {
        handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (mobileNumber.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (isSignup && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      // Use unified signin-or-signup endpoint
      // This endpoint automatically handles both signup and login
      const payload = {
        mobileNumber: mobileNumber,
        password: password,
        username: mobileNumber // Optional: can add username field if user provides it
      };

      console.log('Attempting authentication with:', { mobileNumber, passwordLength: password.length });
      const response = await api.post('signin-or-signup', payload);

      console.log('Authentication response:', {
        success: response.data.success,
        isNewUser: response.data.isNewUser,
        message: response.data.message
      });

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      }

      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      // Show success message
      const successMessage = response.data.isNewUser
        ? 'Account created successfully! Logging you in...'
        : 'Logged in successfully!';

      console.log('Success:', successMessage);

      // Show success and redirect
      setTimeout(() => {
        onClose();
        setMobileNumber('');
        setPassword('');
        setConfirmPassword('');
        setIsSignup(false);
        if (onLoginSuccess) {
          onLoginSuccess(response.data);
        }
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Auth Error:', error);
      const errorMessage = error.response?.data?.message || 'Authentication failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      title: "Experience Free Forever",
      desc: "Experience without any subscription fees"
    },
    {
      title: "Best offers",
      desc: "Enjoy unbeatable offers after login"
    },
    {
      title: "100% Secure & Spam Free",
      desc: "Your data is safeguarded and spam-free"
    }
  ];

  return (
    <Dialog
      open={isOpen || false}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          m: 2,
          maxHeight: isMobile ? '90vh' : 'none'
        }
      }}
    >
      <Grid container sx={{ maxHeight: isMobile ? '90vh' : 'none', overflow: 'auto' }}>
        {/* Left Section */}
        <Grid 
          item 
          xs={12} 
          md={6} 
          sx={{ 
            bgcolor: '#E6A817', 
            position: 'relative', 
            p: isMobile ? 2 : 4,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <IconButton
  onClick={onClose}
  sx={{
    position: 'absolute',
    right: 8,
    top: 8,
    '& svg': {  // Target the SVG directly
      fill: '#ffffff',
      opacity: 1
    }
  }}
>
  <CloseIcon />
</IconButton>

          <Box sx={{ 
            textAlign: 'center', 
            mb: isMobile ? 3 : 8,
            mt: isMobile ? 2 : 0
          }}>
            <Box 
              component="img"
              src="/bgc.png"
              alt="Logo"
              sx={{ 
                width: isMobile ? 120 : 150,
                height: isMobile ? 51 : 64,
                mb: isMobile ? 2 : 3
              }}
            />
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              sx={{ color: 'white', fontWeight: 'bold' }}
            >
              Login now to access best offers!
            </Typography>
          </Box>

          <Grid container spacing={isMobile ? 1 : 2}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper 
                  elevation={0}
                  sx={{
                    p: isMobile ? 1.5 : 2,
                    bgcolor: 'transparent',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 2,
                    textAlign: 'center',
                    height: '100%'
                  }}
                >
                  <StarIcon sx={{ 
                    color: '#FFD700', 
                    fontSize: isMobile ? 24 : 32, 
                    mb: 1 
                  }} />
                  <Typography 
                    variant={isMobile ? "body1" : "subtitle1"} 
                    sx={{ color: 'white', fontWeight: 'bold', mb: 0.5 }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.8)',
                      fontSize: isMobile ? '0.75rem' : '0.875rem'
                    }}
                  >
                    {feature.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Right Section */}
        <Grid item xs={12} md={6}>
          <Box sx={{ p: isMobile ? 2 : 4 }}>
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 'bold', mb: isMobile ? 2 : 4 }}>
              {showResetPassword ? 'Reset Password' : isSignup ? 'Create Account' : 'Login'}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {showResetPassword ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 3 }}>
                <TextField
                  fullWidth
                  placeholder="Enter your email address"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  onKeyPress={handleKeyPress}
                  variant="outlined"
                  error={!!error}
                  disabled={isResetLoading}
                  autoComplete="email"
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleResetPassword}
                  disabled={!email || isResetLoading}
                  sx={{
                    bgcolor: '#E6A817',
                    '&:hover': { bgcolor: '#055459' },
                    '&:disabled': { bgcolor: '#ccc' }
                  }}
                >
                  {isResetLoading ? 'Sending Reset Code...' : 'Send Reset Code'}
                </Button>
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Remember your password?{' '}
                    <Box
                      component="span"
                      onClick={() => {
                        setShowResetPassword(false);
                        setError('');
                        setEmail('');
                      }}
                      sx={{
                        color: '#E6A817',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Back to Login
                    </Box>
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
                sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 3 }}
              >
                {isSignup && (
                  <TextField
                    fullWidth
                    placeholder="Enter your email (optional)"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError('');
                    }}
                    onKeyPress={handleKeyPress}
                    variant="outlined"
                    disabled={isLoading}
                    autoComplete="email"
                  />
                )}
              <TextField
                fullWidth
                placeholder="Enter 10-digit number"
                value={mobileNumber}
                onChange={handleMobileNumberChange}
                onKeyPress={handleKeyPress}
                variant="outlined"
                error={!!error}
                disabled={isLoading}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        +91
                      </Typography>
                    </InputAdornment>
                  )
                }}
                inputProps={{ maxLength: 10 }}
              />

              <TextField
                fullWidth
                placeholder="Enter Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                onKeyPress={handleKeyPress}
                variant="outlined"
                error={!!error}
                disabled={isLoading}
                autoComplete={isSignup ? 'new-password' : 'current-password'}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isLoading}
                      type="button"
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </IconButton>
                  )
                }}
              />

              {isSignup && (
                <TextField
                  fullWidth
                  placeholder="Confirm Password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError('');
                  }}
                  onKeyPress={handleKeyPress}
                  variant="outlined"
                  error={!!error}
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={notifyUpdates}
                    onChange={(e) => setNotifyUpdates(e.target.checked)}
                    disabled={isLoading}
                  />
                }
                label="Notify me for any updates & offers"
              />

              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={
                  mobileNumber.length !== 10 ||
                  password.length < 6 ||
                  (isSignup && password !== confirmPassword) ||
                  isLoading
                }
                sx={{
                  bgcolor: '#E6A817',
                  '&:hover': { bgcolor: '#055459' },
                  '&:disabled': { bgcolor: '#ccc' }
                }}
              >
                {isLoading
                  ? isSignup
                    ? 'Creating Account...'
                    : 'Logging in...'
                  : isSignup
                  ? 'Create Account'
                  : 'Login'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
                  <Box
                    component="span"
                    onClick={() => {
                      setIsSignup(!isSignup);
                      setError('');
                      setPassword('');
                      setConfirmPassword('');
                      setEmail('');
                    }}
                    sx={{
                      color: '#E6A817',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {isSignup ? 'Login' : 'Sign Up'}
                  </Box>
                </Typography>
                {!isSignup && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                    <Box
                      component="span"
                      onClick={() => {
                        setShowResetPassword(true);
                        setError('');
                      }}
                      sx={{
                        color: '#E6A817',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Forgot Password?
                    </Box>
                  </Typography>
                )}
              </Box>
            </Box>
            )}


            <Box sx={{ 
              mt: 2, 
              pt: isMobile ? 4 : 10, 
              textAlign: 'center'
            }}>
              <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                I accept that I have read & understood{' '}
                <Box component="a" href="#" sx={{ color: 'primary.main' }}>
                  Privacy Policy
                </Box>
                {' '}and{' '}
                <Box component="a" href="#" sx={{ color: 'primary.main' }}>
                  T&Cs
                </Box>
              </Typography>
              <Typography variant="body2">
                <Box 
                  component="a" 
                  href="#" 
                  sx={{ 
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Trouble logging in?
                </Box>
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default LoginModal;